import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '@/components/organisms/DataTable';
import type { DataTableColumn } from '@/components/organisms/DataTable';
import { Input } from '@/components/atoms/Input';
import { Select } from '@/components/atoms/Select';
import { Badge } from '@/components/atoms/Badge';
import { Avatar } from '@/components/atoms/Avatar';
import { Button } from '@/components/atoms/Button';
import { Card } from '@/components/molecules/Card';
import { InviteDialog } from '@/components/organisms/InviteDialog';
import { ImportStudentsDialog } from '@/components/organisms/ImportStudentsDialog';
import { PendingInvitations } from '@/components/organisms/PendingInvitations';
import { buildRoute, ROUTES } from '@/constants/routes';
import { useEmployees } from '@/hooks/useCompanyData';
import { useCompanyScope } from '@/hooks/useCompanyScope';
import type { Employee } from '@/types/company.types';
import styles from './EmployeesPage.module.scss';

/** Un `company_admin` no es alumno: no tiene nivel, historias ni XP. */
function isLearner(employee: Employee): boolean {
  return employee.role === 'student';
}

function roleLabel(employee: Employee): string {
  if (employee.role === 'company_admin') return 'Admin';
  if (employee.isCompanyAdmin) return 'Alumno + admin';
  return 'Alumno';
}

export function EmployeesPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [invitationsRefreshKey, setInvitationsRefreshKey] = useState(0);
  const [employeesRefreshKey, setEmployeesRefreshKey] = useState(0);
  const { employees, isLoading } = useEmployees(undefined, undefined, employeesRefreshKey);
  // Si es backoffice gestionando una empresa, las invitaciones van a esa
  // empresa; para un company_admin el backend usa siempre la suya.
  const companyScope = useCompanyScope();

  const filteredEmployees = employees.filter((employee) => {
    const matchesSearch =
      employee.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      employee.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && employee.isActive) ||
      (statusFilter === 'inactive' && !employee.isActive);
    const matchesLevel =
      levelFilter === 'all' || (isLearner(employee) && employee.level === levelFilter);

    return matchesSearch && matchesStatus && matchesLevel;
  });

  if (isLoading) {
    return (
      <div className={styles.employeesPage}>
        <p>Loading employees...</p>
      </div>
    );
  }

  const columns: DataTableColumn<Employee>[] = [
    {
      key: 'employee',
      header: 'Employee',
      accessor: (row) => (
        <div className={styles.employeesPage__employeeCell}>
          <Avatar src={row.avatar} alt={row.name} fallback={row.name} size="md" />
          <div className={styles.employeesPage__employeeInfo}>
            <span className={styles.employeesPage__employeeName}>{row.name}</span>
            <span className={styles.employeesPage__employeeEmail}>{row.email}</span>
          </div>
        </div>
      ),
      sortable: true,
      width: '30%',
    },
    {
      key: 'role',
      header: 'Rol',
      accessor: (row) => (
        <Badge variant={isLearner(row) ? 'default' : 'info'}>{roleLabel(row)}</Badge>
      ),
      sortable: true,
      align: 'center',
    },
    {
      key: 'level',
      header: 'Level',
      accessor: (row) => (isLearner(row) ? <Badge variant="default">{row.level}</Badge> : '—'),
      sortable: true,
      align: 'center',
    },
    {
      key: 'stories',
      header: 'Stories',
      accessor: (row) => (isLearner(row) ? row.stats.storiesCompleted : '—'),
      sortable: true,
      align: 'center',
    },
    {
      key: 'xp',
      header: 'Total XP',
      accessor: (row) => (isLearner(row) ? row.stats.totalXP.toLocaleString() : '—'),
      sortable: true,
      align: 'right',
    },
    {
      key: 'status',
      header: 'Status',
      accessor: (row) => (
        <Badge variant={row.isActive ? 'success' : 'default'}>
          {row.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
      sortable: true,
      align: 'center',
    },
  ];

  const handleRowClick = (employee: Employee) => {
    navigate(buildRoute(ROUTES.COMPANY.EMPLOYEE_DETAIL, { id: employee.id }));
  };

  return (
    <div className={styles.employeesPage}>
      {/* Header */}
      <div className={styles.employeesPage__header}>
        <div>
          <h1 className={styles.employeesPage__title}>Employees</h1>
          <p className={styles.employeesPage__subtitle}>
            Manage and view employee progress and performance
          </p>
        </div>
        <div className={styles.employeesPage__headerActions}>
          {/* La importación masiva es solo para backoffice gestionando una
              empresa: el endpoint vive bajo /backoffice. */}
          {companyScope && (
            <Button variant="secondary" onClick={() => setImportOpen(true)}>
              Import CSV
            </Button>
          )}
          <Button variant="primary" onClick={() => setInviteOpen(true)}>
            Invite
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className={styles.employeesPage__filters}>
        <div className={styles.employeesPage__filtersGrid}>
          <Input
            type="search"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.employeesPage__searchInput}
          />
          <Select
            placeholder="All Statuses"
            value={statusFilter}
            onValueChange={setStatusFilter}
            options={[
              { label: 'All Statuses', value: 'all' },
              { label: 'Active', value: 'active' },
              { label: 'Inactive', value: 'inactive' },
            ]}
          />
          <Select
            placeholder="All Levels"
            value={levelFilter}
            onValueChange={setLevelFilter}
            options={[
              { label: 'All Levels', value: 'all' },
              { label: 'A1', value: 'A1' },
              { label: 'A2', value: 'A2' },
              { label: 'B1', value: 'B1' },
              { label: 'B2', value: 'B2' },
              { label: 'C1', value: 'C1' },
              { label: 'C2', value: 'C2' },
            ]}
          />
        </div>
      </Card>

      {/* Employees Table */}
      <DataTable
        columns={columns}
        data={filteredEmployees}
        onRowClick={handleRowClick}
        emptyMessage="No employees found"
      />

      {/* Invitations */}
      <PendingInvitations companyId={companyScope?.id} refreshKey={invitationsRefreshKey} />

      <InviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        companyId={companyScope?.id}
        allowRoleSelection={Boolean(companyScope)}
        onInvited={() => setInvitationsRefreshKey((k) => k + 1)}
      />

      {companyScope && (
        <ImportStudentsDialog
          open={importOpen}
          onOpenChange={setImportOpen}
          companyId={companyScope.id}
          companyName={companyScope.name}
          existingEmails={employees.map((e) => e.email)}
          onImported={() => setEmployeesRefreshKey((k) => k + 1)}
        />
      )}
    </div>
  );
}

export default EmployeesPage;
